'use client'
import {
    Fragment,
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { ProdutListItem } from '@/eshop/components/ProductList/ListItem'
import { ProductListItem } from '@/eshop/components/ProductList/types'
import Pagination from '@/eshop/components/Pagination'
import { useQuery } from '@tanstack/react-query'
import {
    ProductGroupsConnectionEdge,
    ProductGroupSort_Field,
    SortOrder,
} from '@/eshop/gql/graphql'
import { getProductGroupsRequest, ProductGroupsResponse } from '@/eshop/queries'
import Loader from '@/eshop/components/Loader'
import { languageIdToLocale, PAGE_SIZE } from '@/eshop/constants'
import { getFirstL, shouldRenderFirstLetter } from './ProductList/utils'
import { EshopContext, EshopContextCategory } from './EshopProvider'
import { useTranslation } from 'react-i18next'
import { Heading } from '@/components/Heading'
import { useK2Setup } from '../hooks/useK2Setup'

function getRequest(
    categoryId: string | null,
    languageId: number
): Promise<ProductGroupsResponse> {
    return getProductGroupsRequest({
        filter: [
            {
                products_categories_id: {
                    eq: categoryId,
                },
                languageId: {
                    eq: languageId,
                },
            },
        ],
        after: '',
        before: '',
        first: 10000,
        sort: [
            {
                field: ProductGroupSort_Field.SortingNameLang,
                order: SortOrder.Asc,
            },
        ],
    })
}

export interface ProductListProps {
    initialData: ProductGroupsResponse
    className?: string
    letter: string
    setLetter: (l: string) => void
}

export default function ProductList({
    initialData,
    className,
    letter,
    setLetter,
}: ProductListProps) {
    const [openRow, setOpenRow] = useState<ProductListItem['id'][]>([])
    const [page, setPage] = useState(1)
    const { t } = useTranslation()
    const k2 = useK2Setup()
    const prevCategoryId = useRef<string | null>(null)

    const { categories, setCategories } = useContext(EshopContext)
    const selectedCategoryId = useMemo(() => {
        return categories.reduce<string | null>(
            (acc, cat) => cat?.id ?? acc,
            null
        )
    }, [categories])

    const [categoryId, selectedCategory] = useMemo(
        () =>
            categories.reduce(
                (acc, c) => {
                    if (c.id) {
                        return [c.id, c]
                    }
                    return acc
                },
                [null, undefined] as [
                    categoryId: string | null,
                    category: EshopContextCategory | undefined,
                ]
            ),
        [categories]
    )

    useEffect(() => {
        setOpenRow([])
        setPage(1)
    }, [categories, letter])

    const {
        data: rawData,
        isFetching,
        isFetched,
    } = useQuery({
        queryKey: ['product-groups', categoryId, k2.languageId],
        queryFn: () => getRequest(categoryId, k2.languageId),
        initialData,
    })

    const filteredEdges = useMemo(() => {
        let edges = rawData?.productGroups?.edges || []
        if (letter) {
            edges = edges.filter((product) =>
                filterByLetter(
                    letter.toLowerCase(),
                    product.node.sortingNameLang.toLowerCase()
                )
            )
        }

        return [...edges].sort((a, b) => {
            return a.node.sortingNameLang.localeCompare(
                b.node.sortingNameLang,
                languageIdToLocale[k2.languageId]
            )
        })
    }, [rawData, letter, k2.languageId])

    const total = Math.ceil(filteredEdges.length / PAGE_SIZE)

    useEffect(() => {
        if (rawData) {
            console.log(
                `Number of products: ${rawData.productGroups.edges
                    ?.length}, language: ${
                    languageIdToLocale[k2.languageId]
                }, languageId: ${k2.languageId}`
            )
        }
    }, [rawData, k2.languageId])

    useEffect(() => {
        if (prevCategoryId.current !== categoryId && categoryId && isFetched) {
            const hasData = (rawData?.productGroups?.edges?.length ?? 0) > 0
            const hasFilteredData = filteredEdges.length > 0

            if (letter && !hasFilteredData && hasData) {
                const sortedRaw = [
                    ...(rawData?.productGroups?.edges || []),
                ].sort((a, b) =>
                    a.node.sortingNameLang.localeCompare(
                        b.node.sortingNameLang,
                        languageIdToLocale[k2.languageId]
                    )
                )
                const firstItem = sortedRaw[0]
                if (firstItem?.node?.sortingNameLang) {
                    const newLetter = firstItem.node.sortingNameLang
                        .charAt(0)
                        .toUpperCase()
                    if (newLetter !== letter) {
                        setLetter(newLetter)
                    }
                }
            }
        }
        prevCategoryId.current = categoryId
    }, [
        categoryId,
        letter,
        filteredEdges.length,
        rawData,
        isFetched,
        setLetter,
        k2.languageId,
    ])

    function filterByLetter(letter: string, productName: string): boolean {
        if (letter === '0-#') {
            return !!productName.match(/^[0-9_.-]/)
        }

        const letterDiacritic: Record<string, string[]> = {
            c: ['č'],
            r: ['ř'],
            s: ['š'],
            z: ['ž'],
            a: ['á'],
            e: ['é'],
            i: ['í'],
            o: ['ó', 'ö', 'ő'],
            u: ['ú', 'ü', 'ű'],
            d: ['ď'],
            t: ['ť'],
            n: ['ň'],
            y: ['ý'],
        }

        const lowerLetter = letter
        const lowerName = productName

        let result = lowerName.startsWith(lowerLetter)

        if (!result && letterDiacritic[lowerLetter]) {
            result = letterDiacritic[lowerLetter].some((l) =>
                lowerName.startsWith(l)
            )
        }

        return letter === 'c' ? result && !productName.startsWith('ch') : result
    }

    useEffect(() => {
        const totalCount = filteredEdges.length
        if (
            selectedCategory &&
            selectedCategory.productCount !== totalCount &&
            isFetched
        ) {
            setCategories(
                categories.map((c) => {
                    if (c.id !== categoryId) {
                        return c
                    }
                    return {
                        ...c,
                        productCount: totalCount,
                    }
                })
            )
        }
    }, [
        categories,
        categoryId,
        filteredEdges.length,
        isFetched,
        selectedCategory,
        setCategories,
    ])

    const renderItem = useCallback(
        (
            edgeItem: ProductGroupsConnectionEdge,
            i: number,
            arr: ProductGroupsConnectionEdge[]
        ) => {
            const item = edgeItem.node

            const firstLetter = getFirstL(
                item.sortingNameLang,
                k2.languageId === 5
            )

            // Render first letter of Product if starts with different letter
            const renderLetter = shouldRenderFirstLetter(
                item.sortingNameLang,
                arr[i - 1]?.node?.sortingNameLang,
                i,
                k2.languageId === 5
            )

            const handleItemClick = () =>
                setOpenRow((state) =>
                    state.includes(item.id)
                        ? state.filter((id) => id !== item.id)
                        : [...state, item.id]
                )

            return (
                <Fragment key={item.id}>
                    {(i === 0 || renderLetter) && (
                        <ProductListLetter>{firstLetter}</ProductListLetter>
                    )}
                    <ProdutListItem
                        selectedCategoryId={selectedCategoryId}
                        item={item}
                        isOpen={openRow.includes(item.id)}
                        onClick={handleItemClick}
                    />
                </Fragment>
            )
        },
        [k2.languageId, selectedCategoryId, openRow]
    )

    return (
        <div className={className}>
            <Loader
                isLoading={
                    isFetching ||
                    (!!categoryId && isFetching) ||
                    (!rawData && !isFetched)
                }
            >
                <>
                    {selectedCategory && (
                        <div className="text-center">
                            <Heading
                                tag="h3"
                                align="center"
                                text={selectedCategory.name}
                            />
                            {typeof selectedCategory.productCount ===
                                'number' && (
                                <span>
                                    {selectedCategory.productCount}{' '}
                                    {t('eshop.productlist.productCount')}
                                </span>
                            )}
                        </div>
                    )}
                    {(
                        filteredEdges.slice(
                            (page - 1) * PAGE_SIZE,
                            (page - 1) * PAGE_SIZE + PAGE_SIZE
                        ) || []
                    ).map(renderItem)}
                </>
            </Loader>
            {filteredEdges.length === 0 && (
                <div className="my-6 text-center">
                    {t('eshop.productlist.noProducts')}
                </div>
            )}
            {page > 0 && total > 0 && (
                <Pagination
                    onPageChange={setPage}
                    current={page}
                    total={total}
                    className="mt-24"
                />
            )}
        </div>
    )
}

export function ProductListLetter({ children }: PropsWithChildren<{}>) {
    return <div className="text-lg2 my-5 pl-2 desktop:pl-0">{children}</div>
}
